/**
 * Credits page — a dry, plainspoken accounting of who did what on the
 * Incredible Gamemeisters' Guild website.  Two columns: "Andrew Green
 * (the human)" on the left, "Emergent AI (the agent)" on the right.
 * Same sub-canvas card aesthetic as the homepage Rules / Announcements
 * panels.
 */
import { useNavigate } from "react-router-dom";
import TopBar from "@/components/TopBar";
import NavPane from "@/components/NavPane";
import { YoutubeIcon, SoundcloudIcon, TwitterXIcon, LinkedInIcon, GithubIcon } from "@/components/SocialIcons";

const ACCENT = "#E5C870";
const INK = "#F5F1E8";
const MUTED = "#8E8368";

// Per user iter 64: identical footer to the homepage's contact strip.
const SocialLink = ({ href, label, children, testId }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={label}
    title={label}
    className="inline-flex items-center justify-center w-10 h-10 rounded-sm border transition-all"
    style={{ borderColor: "rgba(229,200,112,0.3)", color: "#E5C870" }}
    onMouseEnter={(e) => { e.currentTarget.style.color = "#F5D98A"; e.currentTarget.style.borderColor = "rgba(229,200,112,0.7)"; }}
    onMouseLeave={(e) => { e.currentTarget.style.color = "#E5C870"; e.currentTarget.style.borderColor = "rgba(229,200,112,0.3)"; }}
    data-testid={testId}
  >
    {children}
  </a>
);

const Section = ({ title, items, testId }) => (
  <section
    className="mb-6 p-5 rounded-sm border"
    style={{ borderColor: "rgba(229,200,112,0.18)", background: "rgba(255,255,255,0.015)" }}
    data-testid={testId}
  >
    <div
      className={items && items.length > 0 ? "text-lg mb-3" : "text-lg"}
      style={{
        color: ACCENT,
        fontFamily: "'Times New Roman', Times, serif",
        fontWeight: 700,
      }}
    >
      {title}
    </div>
    {items && items.length > 0 ? (
      <ul className="list-disc pl-5 space-y-1.5" style={{ color: "#D6CCB4", lineHeight: 1.7 }}>
        {items.map((it, idx) => (
          <li
            key={idx}
            style={{
              fontFamily: "'Times New Roman', Times, serif",
              fontStyle: "normal",
              fontWeight: 400,
              fontSize: "1rem",
            }}
          >
            {it}
          </li>
        ))}
      </ul>
    ) : null}
  </section>
);

export default function CreditsPage() {
  const navigate = useNavigate();

  const HUMAN_ITEMS = [
    "Direction, scope and editorial voice for the entire site — every page, every game, every sub-canvas.",
    "Product decisions: which games to ship (Wu-Pong, Bong, Chess, Pente, Checkers, 15-Tile Puzzle), what to cut (the "
      + "1000 card-game prototype was struck from the codebase), which features ride and which wait.",
    "Aesthetic and typography spec — the per-page font assignments (Times New Roman / IM Fell English / Lucida Console "
      + "/ JetBrains Mono / Cinzel / Textura blackletter) and every nudge to weight, tracking and size.",
    "All bio copy, announcements, the Tavern rules, and the gallery title.",
    "Curatorial direction for the 'Cursed Hereafter' Gallery — every brief that ends up shaping a portrait.",
    "Quality-control: identifying when a font reads wrong, a button sits off-centre, music drifts too atonal, "
      + "or a portrait misses the brief; sending the prototype back for another pass.",
    "All real-world identities and external links — SoundCloud, YouTube, X, LinkedIn, GitHub, email.",
  ];

  const AGENT_ITEMS = [
    "All implementation: React component code, FastAPI endpoints, WebSocket multiplayer for Chess / Pente / Checkers / "
      + "Tavern / Wu-Pong, the 15-puzzle solvability logic, and the chess-rules engine integration via chess.js.",
    "All procedural WebAudio backdrops — the Tavern's vivacious bar score, the Chess minor-key choir, the Pente ambient "
      + "pads, the Checkers medieval estampie, the 15-puzzle phantasmic swooshes, and the dissonant string ensemble in "
      + "the Cursed Hereafter Gallery.",
    "All AI-generated imagery, produced via Google Gemini's Nano Banana model: the anthropomorphic chess pieces, the "
      + "Wu-Pong sprite work, every painted face in the Cursed Hereafter Gallery.",
    "All scoped CSS and the procedural visual flourishes — the 15-puzzle's bluish halo, the Tavern wood grain, the "
      + "Wu-Pong brush lockup, the per-page background gradients and dust overlays.",
    "All glue work: routing, state management, localStorage caching, the leaderboard for the 15-puzzle, "
      + "the match-key handshake for board-game rooms.",
    "Selection, integration and styling of every third-party library powering the site — React for the SPA shell, "
      + "FastAPI for the backend, chess.js for chess move legality, Tailwind for atomic styling, shadcn/ui for the "
      + "primitive component layer, and lucide-react for the icon set; each carries its own upstream credits and "
      + "licences.",
  ];

  return (
    <div
      className="studio-home relative min-h-screen overflow-x-clip"
      data-testid="credits-page"
      style={{ background: "#0C0A08", color: INK }}
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 55% 40% at 22% 18%, rgba(229,200,112,0.09), transparent 65%), radial-gradient(ellipse 60% 45% at 82% 82%, rgba(122,31,31,0.10), transparent 70%)",
        }}
      />
      <TopBar
        theme="studio"
        onBack={() => navigate("/")}
        backLabel="HOME"
        testId="credits-back-button"
      />

      <div className="relative z-10 min-h-screen flex flex-col md:flex-row max-w-7xl mx-auto">
        <div
          className="md:w-[260px] md:shrink-0 md:min-h-screen flex flex-col pt-4 md:pt-6 px-4 md:px-6 pb-6 md:border-r"
          style={{ borderColor: "rgba(229,200,112,0.14)" }}
          data-testid="credits-left-pane"
        >
          <NavPane theme="studio" />
        </div>

        <main className="flex-1 flex flex-col pt-4 md:pt-6 px-6 md:px-12 pb-10">
          <div className="max-w-3xl">
            <h1
              className="leading-[1.02] mb-7"
              style={{
                fontFamily: "'Times New Roman', Times, serif",
                fontWeight: 500,
                color: ACCENT,
                fontSize: "clamp(2.75rem, 5.5vw, 5.5rem)",
                letterSpacing: "0.005em",
                textShadow: "0 2px 0 rgba(0,0,0,0.55), 0 0 28px rgba(229,200,112,0.20)",
              }}
              data-testid="credits-title"
            >
              Credits
            </h1>

            <p
              className="max-w-3xl mb-10"
              style={{
                fontFamily: "'Times New Roman', Times, serif",
                fontStyle: "normal",
                fontWeight: 400,
                fontSize: "1rem",
                color: "#BAB09A",
                lineHeight: 1.65,
              }}
              data-testid="credits-intro"
            >
              This site is a partnership between a human director and an AI agent.  What follows is an honest split of
              what each of us brought.  The human wrote the briefs, made the calls, and signed off; the agent wrote the
              code, generated the images, and synthesised the music.
            </p>

            <Section
              title="Andrew Green — direction, design, content"
              items={HUMAN_ITEMS}
              testId="credits-human-section"
            />
            <Section
              title="[Your credits here if you want to contribute to the website or any of my games]"
              items={[
                "Music for the Tavern: \u2018Riches d\u2019amour\u2019 \u2014 composed by Guillaume de Machaut, performed by Marc Lewon.",
                "Music for the Tavern: \u2018Ich klag\u2019 \u2014 composed by Oswald von Wolkenstein, performed by Marc Lewon.",
                "Music for the Karn\u00f6ffel game page: \u2018French Suite no. 2 in C minor BWV 813 \u2014 1. Allemande\u2019 \u2014 composed by J. S. Bach, performed by Pierre Hanta\u00ef.",
              ]}
              testId="credits-contributor-section"
            />
            <Section
              title="Emergent AI — programming, scripting, sound and art generation"
              items={AGENT_ITEMS}
              testId="credits-agent-section"
            />

            {/* ===== Contact footer (mirrors the homepage) ===== */}
            <div
              className="pt-6 mt-4 border-t"
              style={{ borderColor: "rgba(229,200,112,0.14)" }}
              data-testid="credits-contact-footer"
            >
              <div className="flex items-center gap-3 mb-3">
                <SocialLink href="https://www.youtube.com/@andrewgreen1970" label="YouTube" testId="credits-youtube-link">
                  <YoutubeIcon size={18} />
                </SocialLink>
                <SocialLink href="https://soundcloud.com/andrewgreenofficial" label="SoundCloud" testId="credits-soundcloud-link">
                  <SoundcloudIcon size={18} />
                </SocialLink>
                <SocialLink href="https://x.com/andrewgreenoff" label="X (Twitter)" testId="credits-twitter-link">
                  <TwitterXIcon size={16} />
                </SocialLink>
                <SocialLink href="https://www.linkedin.com/in/andrew-green-572193146/" label="LinkedIn" testId="credits-linkedin-link">
                  <LinkedInIcon size={18} />
                </SocialLink>
                <SocialLink href="https://github.com/andrewgreen76/" label="GitHub" testId="credits-github-link">
                  <GithubIcon size={18} />
                </SocialLink>
              </div>
              <a
                href="mailto:andrewgreenofficial@gmail.com"
                className="lowercase"
                style={{ color: "#E5C870", fontFamily: "'Times New Roman', Times, serif", textTransform: "lowercase", fontSize: "12pt", letterSpacing: "0.04em" }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "#F5D98A")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "#E5C870")}
                data-testid="credits-email-link"
              >
                andrewgreenofficial@gmail.com
              </a>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
